import React from "react";
import { useSelector } from "react-redux";
import { TicketIcon } from "lucide-react";
import { useIsLoggedIn } from "@dynamic-labs/sdk-react-core";
import isNil from "lodash.isnil";

import UsdcIcon from "@/assets/svg/usdc-stroke.svg?react";
import { formatBalance, formatCurrency } from "@/helpers";
import { RootState } from "@/store";
import { useSidebar } from "@/contexts/SidebarContext";
import { cn } from "@/helpers/tailwind";
import { Skeleton } from "@/components/ui";
import { WithTooltip } from "@/components/main";
import { useTicketBalance, useTranslate } from "@/hooks";

interface BalancesProps {
  className?: string;
}

export const Balances: React.FC<BalancesProps> = ({ className }) => {
  const t = useTranslate();
  const isLoggedIn = useIsLoggedIn();
  const { isExpanded } = useSidebar();
  const { balance } = useSelector((state: RootState) => state.wallet);
  const ticketBalance = useTicketBalance();
  const balances = React.useMemo(
    () => [
      {
        type: "balance",
        Icon: UsdcIcon,
        label: t("menu.balance"),
        value: balance,
      },
      {
        type: "tickets",
        Icon: TicketIcon,
        label: t("menu.tickets"),
        value: ticketBalance,
      },
    ],
    [balance, ticketBalance, t]
  );

  const renderAmount = (value: number | string | null, type: string) => {
    if (isNil(value) || !isLoggedIn || type === "") {
      return <Skeleton className="h-4 w-8 bg-slate-200/30" />;
    }
    if (Number(value) === 0) {
      return "-";
    }
    return (
      <WithTooltip
        tooltipContent={`${type === "tickets" ? "$" : ""}${formatCurrency(Number(value), 6)}`}>
        <span className="whitespace-nowrap text-xs font-semibold xl:text-sm">
          {type === "tickets" && "$ "}
          {formatBalance(Number(value))}
        </span>
      </WithTooltip>
    );
  };

  return (
    <div
      className={cn("flex w-full items-stretch gap-1", className, {
        "flex-col": !isExpanded,
      })}>
      {balances.map(({ type, label, value, Icon }, index) => (
        <div
          key={index}
          className={cn(
            "flex w-full flex-col items-start gap-0.5 rounded-lg px-2 py-1.5",
            "bg-slate-800 text-slate-400"
          )}>
          <div className="flex items-center gap-0.5">
            {!isExpanded && <Icon className="size-3" />}
            <span
              className={cn("truncate text-[10px]", {
                block: isExpanded,
              })}>
              {label}
            </span>
          </div>
          <div className="flex items-center gap-2 text-lightblue">
            {isExpanded && <Icon className="size-3.5" />}
            <span>{renderAmount(value, type)}</span>
          </div>
        </div>
      ))}
    </div>
  );
};
